import {
  ModalV2,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalAction,
  Text,
  Flex,
  IconInCircle,
  useBreakpoint,
  useThemeColors,
  ColorsV2,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import ABMSurvey from '@/components/ABMManagement/ABMSurvey';
import { ABMStatus, SurveyOptions } from '@/components/ABMManagement/types';
import { useState, useEffect } from 'react';
import { ABMSetupComponent } from '@/components/ABMManagement/ABMSetupForm';
import { useABMSetup } from '@/components/ABMManagement/useABMSetup';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useUpdateABMOrganizationInfoMutation } from '@/redux/slices/organizationApiSlice';
import { ABMEnrollmentStatusEnum } from '@electricjs/core_entity-client';

type ABMModalProps = {
  onClose: () => void;
};

const ABMModal = ({ onClose }: ABMModalProps) => {
  const { t } = useTranslation('getting-started');
  const { md } = useBreakpoint();
  const organizationId = useGetOrganizationId();
  const [abmStatus, setAbmStatus] = useState<keyof typeof ABMStatus>(
    ABMStatus.DEFAULT
  );
  const [optionChecked, setOptionChecked] = useState<
    keyof typeof SurveyOptions
  >(SurveyOptions.YES);
  const [nextButtonText, setNextButtonText] = useState(
    t('abmModal.buttons.next')
  );
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const [iconColor, iconBackgroundColor] = useThemeColors([
    ColorsV2.INFO,
    ColorsV2.INFO_LIGHTEST,
  ]);

  const [updateABMOrganizationInfo] = useUpdateABMOrganizationInfoMutation();

  const onCloseAbm = () => {
    setAbmStatus(ABMStatus.DEFAULT);
    onClose();
  };

  const onSaveAbm = () => {
    showSuccessToast({
      id: 'abm-save-success-toast',
      message: t('abmModal.toasts.saveSuccess'),
    });
    setAbmStatus(ABMStatus.COMPLETED);
    onCloseAbm();
  };

  const onErrorAbm = () => {
    showErrorToast({
      id: 'abm-save-error-toast',
      message: t('abmModal.toasts.saveError'),
    });
    setAbmStatus(ABMStatus.DEFAULT);
  };

  const {
    handleCheckbox,
    handleInput,
    handleSave,
    needsValidAbmID,
    needsThirdParty,
    abmIDErrorMessage,
    handleResetState,
    isLoading,
  } = useABMSetup({
    onSaveAbm,
    onErrorAbm,
    t,
  });

  const onNotAppliedAbm = () => {
    updateABMOrganizationInfo({
      organizationId,
      abmStatus: ABMEnrollmentStatusEnum.NotApplied,
    })
      .unwrap()
      .then(() => {
        showSuccessToast({
          id: 'abm-not-applied-success-toast',
          message: t('abmModal.toasts.notAppliedSuccess'),
        });
        onCloseAbm();
      })
      .catch(() => {
        showErrorToast({
          id: 'abm-not-applied-error-toast',
          message: t('abmModal.toasts.notAppliedError'),
        });
      });
  };

  const onUnconnectedAbm = () => {
    updateABMOrganizationInfo({
      organizationId,
      abmStatus: ABMEnrollmentStatusEnum.Unconnected,
    })
      .unwrap()
      .then(() => {
        onCloseAbm();
      })
      .catch(() => {
        showErrorToast({
          id: 'abm-unconnected-error-toast',
          message: t('abmModal.toasts.unconnectedError'),
        });
      });
  };

  const handleCancel = () => {
    if (abmStatus === ABMStatus.SETUP) {
      setAbmStatus(ABMStatus.DEFAULT);
      handleResetState();
    } else {
      onCloseAbm();
    }
  };

  // Change next button function based on option checked and ABM status
  const handleNextStep = () => {
    if (abmStatus === ABMStatus.SETUP) {
      handleSave();
    } else {
      switch (optionChecked) {
        case SurveyOptions.SKIP:
          onUnconnectedAbm();
          break;
        case SurveyOptions.NO:
          onNotAppliedAbm();
          break;
        case SurveyOptions.YES:
        default:
          setAbmStatus(ABMStatus.SETUP);
          break;
      }
    }
  };

  // Change next button text based on option checked and ABM status
  useEffect(() => {
    if (abmStatus === ABMStatus.SETUP) {
      setNextButtonText(t('abmModal.buttons.save'));
    } else {
      switch (optionChecked) {
        case SurveyOptions.SKIP:
          setNextButtonText(t('abmModal.buttons.assumeRisk'));
          break;
        case SurveyOptions.NO:
          setNextButtonText(t('abmModal.buttons.save'));
          break;
        case SurveyOptions.YES:
        default:
          setNextButtonText(t('abmModal.buttons.next'));
          break;
      }
    }
  }, [abmStatus, optionChecked, t]);

  const disableSaveButton =
    abmStatus === ABMStatus.SETUP && (needsThirdParty || needsValidAbmID);

  return (
    <ModalV2
      ariaLabelledby="apple-business-manager-modal"
      testId="apple-business-manager-modal"
      visible
      hide={onCloseAbm}
      minWidth={md ? '80rem' : undefined}>
      <ModalHeader>
        <Flex
          p="1.5rem 1rem"
          width="100%"
          flexWrap="wrap"
          justifyContent="space-between"
          gap="1rem">
          <Flex>
            <IconInCircle
              color={iconColor}
              background={iconBackgroundColor}
              icon="logo-apple"
              customIconSize="2rem"
              size="3.2rem"
            />
            <Text variant="heading-3" ml="1rem">
              {t('abmModal.title')}
            </Text>
          </Flex>
        </Flex>
      </ModalHeader>
      <ModalBody overflowY="auto" p="0rem">
        {abmStatus === ABMStatus.DEFAULT && (
          <ABMSurvey
            optionChecked={optionChecked}
            onSelectionChange={setOptionChecked}
          />
        )}
        {abmStatus === ABMStatus.SETUP && (
          <ABMSetupComponent
            handleCheckbox={handleCheckbox}
            handleInput={handleInput}
            handleSave={handleSave}
            needsValidAbmID={needsValidAbmID}
            needsThirdParty={needsThirdParty}
            abmIDErrorMessage={abmIDErrorMessage}
            hasFormActionsButtons={false}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions justifyContent={md ? 'end' : 'center'}>
          <ModalAction
            onClick={handleCancel}
            py="1.4rem"
            px="2rem"
            id="modal-abm-cancel-step-button"
            testId="modal-abm-cancel-step-button"
            variant="outline"
            border="none">
            {t('abmModal.buttons.cancel')}
          </ModalAction>
          <ModalAction
            py="1rem"
            px="1.5rem"
            id={`modal-abm-${optionChecked}`}
            testId={`modal-abm-${optionChecked}`}
            onClick={handleNextStep}
            disabled={disableSaveButton}
            loading={abmStatus === ABMStatus.SETUP && isLoading}>
            {nextButtonText}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default ABMModal;
